import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, MailCheck, ArrowLeft } from 'lucide-react';

export default function ForgotPasswordPage() {
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await sendPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-3">
            <img src="/logo.png" alt="ReadTheRoom" className="h-14 w-14 rounded-xl object-cover mx-auto" />
          </Link>
        </div>

        {sent ? (
          <div className="bg-forest/10 border border-forest/20 rounded-2xl px-8 py-10 text-center">
            <MailCheck className="w-12 h-12 text-forest mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-ink mb-3">Check your inbox</h2>
            <p className="text-ink-light text-sm leading-relaxed mb-2">
              We sent a password reset link to
            </p>
            <p className="text-ink font-medium text-sm mb-4">{email}</p>
            <p className="text-ink-muted text-xs leading-relaxed">
              Click the link in the email to choose a new password. It expires in 1 hour.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 mt-6 text-sm text-terra hover:text-terra/80 font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </Link>
          </div>
        ) : (
          <Card className="bg-cream border-cream-dark shadow-sm">
            <CardHeader className="pb-0 pt-6 px-6">
              <h1 className="font-heading text-2xl text-ink">Reset Password</h1>
              <p className="text-ink-muted text-sm mt-1">
                Enter your email and we&apos;ll send you a reset link.
              </p>
            </CardHeader>
            <CardContent className="px-6 pb-6 pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-ink-light text-sm font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full bg-terra hover:bg-terra/90 text-cream font-semibold h-10 mt-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-ink-muted mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-terra hover:text-terra/80 font-medium"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
